import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, CreditCard, ShieldCheck } from "lucide-react";

const PLANS = [
  { id: "professional", title: "Professional", price: "58,80€", per: "all'anno", note: "un'unica soluzione · tutte le funzioni", best: true },
  { id: "standard", title: "Standard", price: "19,90€", per: "all'anno", note: "un'unica soluzione · funzioni essenziali" },
];

export default function Subscription() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [plan, setPlan] = useState(new URLSearchParams(window.location.search).get("plan") === "standard" ? "standard" : "professional");
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(null);
  useEffect(() => { if (user) { api.get("/billing/subscription").then((r) => setEnabled(r.data.paymentsEnabled)).catch(() => {}); } }, [user]);

  if (!user) return <div className="max-w-3xl mx-auto px-6 py-16 text-center"><Link to="/login" className="btn-pill-gold inline-flex">Accedi</Link></div>;
  if (!["artist", "supplier", "business"].includes(user.role)) {
    return <div className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="subscription-not-required"><p className="text-slate-600 mb-4">Il tuo profilo è gratuito: non serve alcun abbonamento.</p><Link to="/dashboard" className="btn-pill-gold inline-flex">Vai alla Dashboard</Link></div>;
  }

  const start = async () => {
    setLoading(true);
    try {
      const r = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile avviare il pagamento");
      setLoading(false);
    }
  };

  const simulate = async () => {
    setLoading(true);
    try {
      await api.post("/billing/simulate", { plan });
      toast.success("Abbonamento attivato in simulazione: trovi la fattura di 1€ nella Dashboard");
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attivazione non riuscita");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <span className="overline text-gold">Abbonamento</span>
      <h1 className="font-display text-4xl font-bold mt-2 mb-3">Provalo gratis per 7 giorni</h1>
      <p className="text-slate-600 mb-8 max-w-2xl">Per attivare la prova inserisci i dati della carta: non paghi nulla per 7 giorni. Poi paghi solo 1€ per i primi 2 mesi, quindi parte il piano che scegli qui sotto. I rinnovi sono automatici e puoi disdire quando vuoi dal tuo profilo con il tasto "Disdici abbonamento".</p>
      {params.get("annullato") && <div className="mb-6 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">Pagamento annullato: nessun addebito. Puoi riprovare quando vuoi.</div>}

      {enabled === false && <div className="mb-6 p-4 rounded-xl bg-gold/10 border border-gold/40 text-sm text-navy" data-testid="payments-disabled">I pagamenti online sono in fase di attivazione: puoi attivare il piano in <b>modalità simulazione</b> (nessun addebito reale, fatture generate come prova).</div>}
      <ol className="grid md:grid-cols-3 gap-4 mb-10">
        {[["Giorni 1-7", "Gratis", "carta richiesta, nessun addebito"], ["Mesi 1-2", "1€", "addebitato a fine prova"], ["Poi", plan === "professional" ? "58,80€/anno" : "19,90€/anno", "rinnovo automatico"]].map(([a, b, c]) => (
          <li key={a} className="card-premium p-5"><div className="overline text-slate-500">{a}</div><div className="font-display text-2xl font-bold text-navy mt-1">{b}</div><div className="text-xs text-slate-500 mt-1">{c}</div></li>
        ))}
      </ol>

      <div className="grid md:grid-cols-2 gap-5 mb-8">
        {PLANS.map((p) => (
          <button key={p.id} type="button" data-testid={`plan-${p.id}`} onClick={() => setPlan(p.id)}
            className={`text-left p-6 rounded-2xl border-2 transition-colors ${plan === p.id ? "border-gold bg-gold/5" : "border-slate-200 bg-white hover:border-gold/50"}`}>
            <div className="flex items-center justify-between">
              <span className="font-display text-xl font-bold">{p.title}</span>
              {p.best && <span className="chip-solid">Più conveniente</span>}
            </div>
            <div className="mt-3"><span className="font-display text-4xl font-bold text-navy">{p.price}</span> <span className="text-slate-500">{p.per}</span></div>
            <div className="text-sm text-slate-600 mt-1">{p.note}</div>
            {plan === p.id && <div className="mt-3 text-gold text-sm font-semibold flex items-center gap-1"><Check className="w-4 h-4" />Selezionato</div>}
          </button>
        ))}
      </div>

      {enabled === false
        ? <button data-testid="subscription-simulate" disabled={loading} onClick={simulate} className="btn-pill-gold text-base disabled:opacity-50"><CreditCard className="w-5 h-5" />{loading ? "Attivazione…" : "Attiva in modalità simulazione"}</button>
        : <button data-testid="subscription-checkout" disabled={loading || enabled !== true} onClick={start} className="btn-pill-gold text-base disabled:opacity-50"><CreditCard className="w-5 h-5" />{loading ? "Apertura pagamento…" : "Inserisci carta e inizia la prova"}</button>}
      <p className="text-xs text-slate-500 mt-4 flex items-center gap-2"><ShieldCheck className="w-4 h-4" />Pagamento sicuro gestito da Stripe. I prezzi sono IVA inclusa.</p>
    </div>
  );
}
